import mapboxgl, { LngLat } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { quartile } from "../../shared/tools";
import { updateSelectedStore } from "../utils.js";
import { scrollToView } from "../utils.js";
import style from "./style.module.scss";

const Controller = ({
  setMap,
  setResellerList,
  setSelectedReseller,
  selectedReseller,
  geojson,
  pendingCenter,
  setPendingCenter,
  setIsLoading,
  seasonColor,
}) => {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const mapContainer = useRef(null);

  const isTablet = useMediaQuery({ query: "(max-width: 992px)" });
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });

  // On click on marker, remove class of actual selected marker and add it to the new selected
  const clickListener = useCallback(
    (e, element, isMapboxEvent = false) => {
      const selectedMarker = document.getElementsByClassName("selectedMarker");
      while (selectedMarker.length > 0) {
        selectedMarker[0].classList.remove("selectedMarker");
      }

      // Si c'est un événement Mapbox, on gère différemment la sélection
      if (isMapboxEvent) {
        // On peut soit gérer la sélection via feature-state
        // soit trouver le marker correspondant via son ID
        const markerElement = document.getElementById("marker_" + element.id);
        if (markerElement) {
          markerElement.classList.add("selectedMarker");
        }
      } else {
        // Cas classique pour les markers HTML
        e.target.classList.add("selectedMarker");
      }

      setSelectedReseller(element);
    },
    [setSelectedReseller]
  );

  // Calculate size of the bounds
  const calculateBoundSize = useCallback(() => {
    if (
      !geojson ||
      !geojson.features ||
      !Array.isArray(geojson.features) ||
      geojson.features.length === 0
    ) {
      return {
        minLong: 0,
        minLat: 0,
        maxLong: 10,
        maxLat: 10,
      };
    }

    // Get all latitude and longitude values
    let latValues = [];
    let longValues = [];

    geojson.features.forEach((element) => {
      const coords = element.geometry.coordinates;
      latValues.push(coords[1]);
      longValues.push(coords[0]);
    });

    // Calculate the low and high quartiles of minimum and maximum values.
    // Median, or quartiles are less sensitive to outlier values.
    const minLat = quartile(latValues, 0.01);
    const maxLat = quartile(latValues, 0.99);
    const minLong = quartile(longValues, 0.01);
    const maxLong = quartile(longValues, 0.99);

    let ecartLat = 0;
    let ecartLong = 0;

    if (isTablet) {
      ecartLat = Math.abs(maxLat - minLat) / 5;
      ecartLong = Math.abs(maxLong - minLong) / 5;
    } else {
      ecartLat = Math.abs(maxLat - minLat) / 3;
      ecartLong = Math.abs(maxLong - minLong) / 3;
    }

    return {
      minLong: minLong + ecartLong,
      minLat: minLat + ecartLat,
      maxLong: maxLong - ecartLong,
      maxLat: maxLat - ecartLat,
    };
  }, [geojson, isTablet]);

  //List actualizer on select
  useEffect(() => {
    const selectedMarker = document.getElementsByClassName("selectedMarker");
    while (selectedMarker.length > 0) {
      selectedMarker[0].classList.remove("selectedMarker");
    }

    if (selectedReseller) {
      const markerElement = document.getElementById(
        "marker_" + selectedReseller.id
      );

      if (markerElement) {
        markerElement.classList.add("selectedMarker");
      }
    }
  }, [selectedReseller]);

  const getClosestStores = useCallback(
    (coord) => {
      if (!geojson || !geojson.features || !Array.isArray(geojson.features)) {
        setResellerList([]);
        return;
      }

      const filteredGeojson = geojson;

      if (
        !Array.isArray(coord) ||
        coord.length < 2 ||
        typeof coord[0] !== "number" ||
        typeof coord[1] !== "number"
      ) {
        console.error("Invalid coordinates:", coord);
        return;
      }

      let coords = new LngLat(coord[0], coord[1]);
      let storesWithDistance = filteredGeojson.features
        .map((element) => {
          let storeCoord = new LngLat(
            element.geometry.coordinates[0],
            element.geometry.coordinates[1]
          );
          return { element, dist: storeCoord.distanceTo(coords) };
        })
        .filter((item) => item.dist <= 20000) // Filtre les magasins dans un rayon de 20 km
        .sort((a, b) => a.dist - b.dist);

      // Met à jour la liste des revendeurs avec les magasins dans un rayon de 20 km
      setResellerList(storesWithDistance.map((item) => item.element));

      scrollToView("biogents-resellers-map");
    },
    [setResellerList, geojson]
  );

  // Actualise source data after each change inside filters
  useEffect(() => {
    if (!mapRef.current) return;
    const sourceData = mapRef.current.getSource("resellers");
    if (sourceData && geojson && geojson.features) {
      sourceData.setData(geojson);
    }
  }, [geojson]);

  useEffect(() => {
    if (!mapRef.current) return;

    const handleMoveEnd = () => {
      if (pendingCenter) {
        getClosestStores(pendingCenter);
        setPendingCenter(null);
      }
      setIsLoading(false);
    };

    mapRef.current.on("moveend", handleMoveEnd);

    return () => {
      mapRef.current.off("moveend", handleMoveEnd);
    };
  }, [pendingCenter, getClosestStores, setPendingCenter, setIsLoading]);

  //Initialize map
  useEffect(() => {
    if (!geojson || mapRef.current) return;

    const mapContain = document.createElement("div");

    const bounds = calculateBoundSize();

    mapRef.current = new mapboxgl.Map({
      container: mapContain,
      accessToken: mapboxgl.accessToken,
      cooperativeGestures: true,
      bounds: [
        [bounds.minLong, bounds.minLat],
        [bounds.maxLong, bounds.maxLat],
      ],
      style: "mapbox://styles/margauxmassonnat/clbowth0o001814qrf70ppnfr",
      locale: {
        "ScrollZoomBlocker.CtrlMessage": t(
          "Use ctrl + scroll to zoom in on the map"
        ),
        "ScrollZoomBlocker.CmdMessage": t(
          "Use ⌘ + scroll to zoom in on the map"
        ),
      },
    });
    mapContain.style.width = "100%";
    mapContain.style.height = "100%";
    mapRef.current.on("load", () => {
      mapRef.current.resize();
    });

    mapContainer.current.appendChild(mapRef.current.getContainer());

    mapRef.current.dragRotate.disable();
    mapRef.current.touchZoomRotate.disableRotation();

    const filteredGeojson = geojson;

    mapRef.current.on("load", () => {
      // Ajoute la source
      mapRef.current.addSource("resellers", {
        type: "geojson",
        data: filteredGeojson,
        cluster: true,
        clusterMaxZoom: 11,
        clusterRadius: 67,
      });

      // Layer pour les clusters
      mapRef.current.addLayer({
        id: "cluster",
        type: "circle",
        source: "resellers",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#0080be",
          "circle-radius": 32,
        },
      });

      mapRef.current.on("click", "cluster", function (e) {
        mapRef.current.flyTo({
          center: e.lngLat,
          zoom: mapRef.current.getZoom() + 2,
        });
      });

      // Layer pour le compteur de clusters
      mapRef.current.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "resellers",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-font": ["DIN Offc Pro Bold", "Arial Unicode MS Bold"],
          "text-size": 18,
        },
        paint: {
          "text-color": "white",
        },
      });

      // Layer pour les points non clusterisés
      mapRef.current.addLayer({
        id: "resellers-unclustered-points",
        type: "circle",
        source: "resellers",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": [
            "case",
            ["==", ["get", "id"], ["coalesce", ["get", "selectedId"], -1]],
            "#0080be",
            "#ffffff",
          ],
          "circle-radius": 5,
          "circle-stroke-color": [
            "case",
            ["==", ["get", "id"], ["coalesce", ["get", "selectedId"], -1]],
            "#ffffff",
            "#0080be",
          ],
          "circle-stroke-width": 10,
        },
      });

      mapRef.current.on("click", "resellers-unclustered-points", (e) => {
        const coordinates = e.features[0].geometry.coordinates;
        const element = e.features[0];

        updateSelectedStore(mapRef.current, element);

        setResellerList((currentResellerList) => {
          const isInResellerList = currentResellerList.some(
            (store) => store.properties.id === element.properties.id
          );

          if (!isInResellerList) {
            return [element];
          }
          return currentResellerList;
        });

        clickListener(e, element, true);

        const longitude =
          isTablet && !isMobile
            ? parseFloat(coordinates[0].toFixed(2)) - 0.15
            : parseFloat(coordinates[0].toFixed(2));

        mapRef.current.easeTo({
          center: [longitude, parseFloat(coordinates[1].toFixed(2))],
          curve: 0.1,
        });

        scrollToView("biogents-resellers-filters");
      });
    });

    setMap(mapRef.current);
  }, [
    mapRef,
    setMap,
    geojson,
    clickListener,
    calculateBoundSize,
    t,
    isTablet,
    seasonColor,
    isMobile,
    setResellerList,
  ]);

  return <div className={style.map} id="biogents-resellers-map" ref={mapContainer} />;
};
export default Controller;
